/**
 * Replica-side search-index maintenance. Mirrors apps/do-workspace's
 * search-indices.ts; the replica calls this on every op accepted into
 * `op_log` so the local indices stay in lockstep with the server's.
 *
 * Schema parity is enforced by the mirrored migrations
 * (packages/replica/src/schema.ts versions 6/7 ↔ apps/do-workspace/migrations
 * 0002 + 0003).
 */
import {
  FACT_FTS_TABLE,
  ENTITY_NAME_INDEX_TABLE,
  NAME_PREDICATE,
  normalizeName,
  type SearchSqlBackend,
  type SearchSqlParam,
} from '@dl/search';
import {
  FactPayloadSchema,
  foldFactChainFull,
  type FactPayload,
} from '@dl/shared';

import { type SqlBackend } from './sql.js';

/** Wrap a replica SqlBackend so it satisfies @dl/search's SearchSqlBackend. */
function adapt(sql: SqlBackend): SearchSqlBackend {
  return {
    exec(sqlStr: string, ...params: SearchSqlParam[]): void {
      sql.exec(sqlStr, ...params);
    },
    all<Row extends Record<string, unknown>>(sqlStr: string, ...params: SearchSqlParam[]): Row[] {
      return sql.all<Row>(sqlStr, ...params);
    },
  };
}

interface ChainRow extends Record<string, unknown> {
  payload: string;
  hlc_wall_ms: number;
  hlc_logical: number;
}

function loadChain(sql: SqlBackend, subject: string, predicate: string): FactPayload[] {
  const rows = sql.all<ChainRow>(
    `SELECT payload, hlc_wall_ms, hlc_logical
       FROM op_log
      WHERE subject = ? AND predicate = ?
      ORDER BY hlc_wall_ms ASC, hlc_logical ASC;`,
    subject,
    predicate,
  );
  const chain: FactPayload[] = [];
  for (const r of rows) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(r.payload);
    } catch {
      continue;
    }
    if (!parsed || typeof parsed !== 'object') continue;
    const kind = (parsed as { kind?: unknown }).kind;
    if (typeof kind !== 'string' || !kind.startsWith('fact.')) continue;
    try {
      chain.push(FactPayloadSchema.parse(parsed));
    } catch {
      // skip malformed
    }
  }
  return chain;
}

/** Update the name + FTS indices for one (subject, predicate) chain. */
export function updateSearchIndices(
  sql: SqlBackend,
  subject: string,
  predicate: string,
  payload: unknown,
): void {
  if (!isFactPayload(payload)) return;
  const adapted = adapt(sql);

  const chain = loadChain(sql, subject, predicate);
  const folded = foldFactChainFull(chain);

  if (predicate === NAME_PREDICATE) {
    if (!folded || folded.soft_deleted || typeof folded.value !== 'string') {
      adapted.exec(
        `DELETE FROM ${ENTITY_NAME_INDEX_TABLE} WHERE entity_id = ?;`,
        subject,
      );
    } else {
      const normalized = normalizeName(folded.value);
      if (normalized.length === 0) {
        adapted.exec(
          `DELETE FROM ${ENTITY_NAME_INDEX_TABLE} WHERE entity_id = ?;`,
          subject,
        );
      } else {
        adapted.exec(
          `INSERT INTO ${ENTITY_NAME_INDEX_TABLE} (entity_id, normalized_name, display_name)
             VALUES (?, ?, ?)
           ON CONFLICT (entity_id) DO UPDATE SET
             normalized_name = excluded.normalized_name,
             display_name    = excluded.display_name;`,
          subject,
          normalized,
          folded.value,
        );
      }
    }
  }

  const factId = `${subject}|${predicate}`;
  adapted.exec(`DELETE FROM ${FACT_FTS_TABLE} WHERE fact_id = ?;`, factId);
  if (folded && !folded.soft_deleted && typeof folded.value === 'string' && folded.value.length > 0) {
    adapted.exec(
      `INSERT INTO ${FACT_FTS_TABLE} (fact_id, entity_id, predicate, text_value)
         VALUES (?, ?, ?, ?);`,
      factId,
      subject,
      predicate,
      folded.value,
    );
  }
}

function isFactPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const kind = (payload as { kind?: unknown }).kind;
  return typeof kind === 'string' && kind.startsWith('fact.');
}
